import { useState, useCallback } from "react";
import type { Task, TaskColumn } from "@/features/tasks/types/task";
import { getTasks, moveTask as moveTaskApi, completeTask as completeTaskApi } from "@/features/tasks/services/taskService";
import { getColumns } from "@/features/tasks/services/taskColumnService";

export type BoardKey = "personal" | string; // string = team_id

interface UseTasksReturn {
  columns: TaskColumn[];
  tasks: Task[];
  loading: boolean;
  error: string | null;
  fetchBoard: (boardKey: BoardKey) => Promise<void>;
  moveTask: (taskId: string, columnId: string, colOrder: number) => Promise<void>;
  refreshTasks: (boardKey: BoardKey) => Promise<void>;
  toggleComplete: (taskId: string) => Promise<void>;
}

export function useTasks(): UseTasksReturn {
  const [columns, setColumns] = useState<TaskColumn[]>([]);
  const [tasks, setTasks]     = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetchBoard = useCallback(async (boardKey: BoardKey) => {
    setLoading(true);
    setError(null);
    try {
      const isPersonal = boardKey === "personal";
      const [cols, tsks] = await Promise.all([
        isPersonal
          ? getColumns("user")
          : getColumns("team", boardKey),
        isPersonal
          ? getTasks({ board: "personal" })
          : getTasks({ team_id: boardKey }),
      ]);
      setColumns(cols.sort((a, b) => a.col_order - b.col_order));
      setTasks(tsks.sort((a, b) => a.col_order - b.col_order));
    } catch (err: any) {
      setError(err?.message ?? "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTasks = useCallback(async (boardKey: BoardKey) => {
    try {
      const isPersonal = boardKey === "personal";
      const tsks = isPersonal
        ? await getTasks({ board: "personal" })
        : await getTasks({ team_id: boardKey });
      setTasks(tsks.sort((a, b) => a.col_order - b.col_order));
    } catch (err: any) {
      setError(err?.message ?? "Failed to refresh tasks");
    }
  }, []);

  const moveTask = useCallback(
    async (taskId: string, columnId: string, colOrder: number) => {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, column_id: columnId, col_order: colOrder } : t,
        ),
      );
      try {
        await moveTaskApi(taskId, { column_id: columnId, col_order: colOrder });
      } catch (err: any) {
        setError(err?.message ?? "Failed to move task");
        // Tasks will resync on next fetchBoard call
      }
    },
    [],
  );

  const toggleComplete = useCallback(async (taskId: string) => {
    try {
      const updated = await completeTaskApi(taskId);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, completed_at: updated.completed_at, completed_by: updated.completed_by }
            : t,
        ),
      );
    } catch (err: any) {
      setError(err?.message ?? "Failed to update task");
    }
  }, []);

  return { columns, tasks, loading, error, fetchBoard, moveTask, refreshTasks, toggleComplete };
}
